import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  useDate,
  useOrderByFilter,
  usePagination,
  useToQueryString,
} from '@/core'
import type { TInvoice, TProjectManagement } from '../../../types'
import { Pagination } from '@/core/types'
import { useInvoicesQuery } from '../query/useInvoicesQuery'
import { useModuleQuery } from '../query/useModuleQuery'

type TInvoicePIHandlers = {
  onAddInvoiceFn?: (payload: TProjectManagement) => void
}

type TInvoicePIOptions = {
  handlers?: TInvoicePIHandlers
}
const [useProvideInvoice, useInvoice] = createInjectionState(
  (options?: TInvoicePIOptions) => {
    const rows = ref<number[]>([])
    const form = ref<TInvoice>({})
    const modelId = ref<number>()
    const { zonedTimeToUtc } = useDate()
    const filters = ref<ToQueryStringFilters>({
      param: '',
      dates: {
        created_at: [],
      },
      order_by: 'created_at,desc',
    })

    const moduleQueryParams = useToQueryString(toReactive(filters), {
      searchColumns: ['invoice_number'],
    })

    const modulePagination = usePagination()

    const moduleQuery = useModuleQuery({
      modelId: computed(() => Number(modelId.value)),
      enableQuery: {
        loadRecords: false,
        loadRecord: computed(() => !!modelId.value),
      },
    })

    const invoiceQuery = useInvoicesQuery({
      modelId: computed(() => Number(modelId.value)),
      currentPage: modulePagination.currentPage,
      filters: {
        loadRecords: moduleQueryParams.queryParams,
      },
      enableQuery: {
        loadRecords: computed(
          () => !!moduleQueryParams.params.value.length && !!modelId.value,
        ),
        loadRecord: false,
      },
      handlers: {
        onDataChange: (pagination: Pagination) => {
          modulePagination.setPagination(pagination)
        },
      },
    })

    const { orderByOptions } = useOrderByFilter({
      columns: ['created_at'],
    })

    const onAdd = async (payload: TInvoice) => {
      await invoiceQuery.addRecord.mutateAsync({
        ...payload,
        date: payload.date
          ? zonedTimeToUtc(new Date(payload.date ?? ''), {
              removeTimeZone: true,
            })
          : payload.date,
      })
      const project = await moduleQuery.loadRecord.refetch()

      if (project.data) {
        options?.handlers?.onAddInvoiceFn?.(project.data)
      }
    }

    const onEdit = async (payload: TInvoice) => {
      await invoiceQuery.editRecord.mutateAsync({
        ...payload,
        date: payload.date
          ? zonedTimeToUtc(new Date(payload.date ?? ''), {
              removeTimeZone: true,
            })
          : payload.date,
      })
      const project = await moduleQuery.loadRecord.refetch()

      if (project.data) {
        options?.handlers?.onAddInvoiceFn?.(project.data)
      }
    }

    const onDelete = async (payload: TInvoice) => {
      await invoiceQuery.deleteRecord.mutateAsync(payload)
      const project = await moduleQuery.loadRecord.refetch()

      if (project.data) {
        options?.handlers?.onAddInvoiceFn?.(project.data)
      }
    }

    const onLoadRecord = async (payload: number) => {
      modelId.value = payload
      invoiceQuery.loadRecords.refetch()
    }

    const onLoadProjects = async () => {
      moduleQuery.loadRecords.refetch()
    }

    const tableData = computed(() => invoiceQuery.loadRecords.data?.value?.data)
    const model = computed(() => moduleQuery.loadRecord?.data.value)

    const isLoading = computed(
      () =>
        invoiceQuery.addRecord.isLoading.value ||
        invoiceQuery.editRecord.isLoading.value ||
        invoiceQuery.loadRecords.isLoading.value,
    )

    return {
      tableData,
      filters,
      orderByOptions,
      rows,
      form,
      isLoading,
      model,
      modulePagination: toReactive(modulePagination),
      onDelete,
      onEdit,
      onAdd,
      onLoadRecord,
      onLoadProjects,
    }
  },
)

export { useProvideInvoice, useInvoice }
